import { useCallback, useEffect, useRef, useState } from 'react';
import { useFeedViewCards } from './hooks/index.js';
import MojitoCore from 'mojito/core';
import MojitoModules from 'mojito/modules';
import MojitoPresentation from 'mojito/presentation';
import { isEmpty, uniq } from 'mojito/utils';

const { arraysAreEqual } = MojitoCore.Base.objUtils;

const EventCardsCarousel = MojitoModules.EventCardsCarousel.module;
const VerticalMatchCard = MojitoModules.EventCards.VerticalMatch.view;
const { useCardsSportFilter } = MojitoModules.EventCardsCarousel.Hooks;
const { PromotionUtils } = MojitoModules.Promotions;
const { useLoadingStatusMetric } = MojitoModules.Performance.Hooks;
const classUtils = MojitoCore.Base.classUtils;
const { FlexPane } = MojitoPresentation.Components;
const EMPTY_SPORT_IDS = [];

export default function FeedView(props) {
    const { mojitoTools, class: className, selectedSportId, onSportsLoad } = props;
    const { config, instanceId } = mojitoTools;
    const { cardsToRender, sportIds, promotionsLoaded } = useFeedViewCards(config);
    const cards = useCardsSportFilter(cardsToRender, selectedSportId);
    const propCardsCarouselConfig = config.propCardsCarousel;
    const showPropCardsCarousel =
        !!propCardsCarouselConfig?.containerId ||
        PromotionUtils.hasLocationId(propCardsCarouselConfig);
    const topCardsCount = showPropCardsCarousel ? config.topCardsCount : cards.length;
    const topCards = cards.slice(0, topCardsCount);
    const restCards = showPropCardsCarousel ? cards.slice(topCardsCount) : [];
    const noContent = promotionsLoaded && isEmpty(cardsToRender);
    const metricClientId = config.reportLoadingStatus ? instanceId : undefined;
    const performanceElementRef = useLoadingStatusMetric(
        promotionsLoaded,
        metricClientId,
        'FeedView',
        noContent
    );
    const [propSportIds, setPropSportIds] = useState(EMPTY_SPORT_IDS);
    const reportedSportIdsRef = useRef(EMPTY_SPORT_IDS);
    const onPropCarouselSportsChange = useCallback(sportIds => setPropSportIds(sportIds), []);

    useEffect(() => {
        if (!showPropCardsCarousel) {
            setPropSportIds(EMPTY_SPORT_IDS);
        }
    }, [showPropCardsCarousel]);

    useEffect(() => {
        if (!promotionsLoaded) {
            return;
        }
        const feedSportIds = uniq([...sportIds, ...propSportIds]);
        if (arraysAreEqual(reportedSportIdsRef.current, feedSportIds)) {
            return;
        }
        reportedSportIdsRef.current = feedSportIds;
        onSportsLoad(feedSportIds);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [promotionsLoaded, sportIds, propSportIds]);

    if (noContent) {
        return null;
    }

    return (
        <FlexPane
            config={config.container}
            class={classUtils.classNames('ta-FeedView', className)}
            elementRef={performanceElementRef}
        >
            <CardList {...props} cards={topCards} indexOffset={0} />
            {showPropCardsCarousel && (
                <EventCardsCarousel
                    config={propCardsCarouselConfig}
                    pageId={config.applicablePage}
                    selectedSportId={selectedSportId}
                    onSportsChange={onPropCarouselSportsChange}
                    class="ta-FeedView-propCardsCarousel"
                />
            )}
            <CardList {...props} cards={restCards} indexOffset={topCardsCount} />
        </FlexPane>
    );
}

FeedView.getStyle = (config, mode, merge) => ({
    featuredEventCard: merge(config.eventCard, config.featuredEventCard),
});

function CardList(props) {
    const { cards, indexOffset, mojitoTools } = props;
    const { config, style } = mojitoTools;

    if (isEmpty(cards)) {
        return null;
    }

    return (
        <FlexPane config={config.cardList} class="ta-cardList">
            {cards.map(({ id, content }, index) => {
                const listIndex = indexOffset + index;
                return (
                    <VerticalMatchCard
                        key={id}
                        config={listIndex === 0 ? style.featuredEventCard : config.eventCard}
                        eventId={content.eventId}
                        marketId={content.marketId}
                        backgroundImageUrl={content.imgUrl}
                    />
                );
            })}
        </FlexPane>
    );
}
